/**
 * Shell del portale (mockup v2): menu laterale per sezione e ruolo, testata con ricerca, aiuto (STD-6), lingua e profilo.
 * Il menu nasce dai permessi del ruolo (PERMESSI di @qa/shared): il commerciale non vede claim né fornitori.
 */
import { Badge, Button, Drawer, Grid, Layout, Menu, Space, Typography, type MenuProps } from "antd";
import {
  AuditOutlined,
  BarChartOutlined,
  CarryOutOutlined,
  CheckSquareOutlined,
  EuroOutlined,
  FileTextOutlined,
  FlagOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloudServerOutlined,
  ProfileOutlined,
  ShopOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { HomeDto, PaginaDto, PropostaRigaDto } from "@qa/shared";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api/client";
import { palette } from "../theme";
import { usePuo } from "../lib/permessi";
import { vistaDi } from "../lib/viste";
import { linguaCorrente } from "../i18n";
import { SelettoreLingua, useLinguaUtente } from "./SelettoreLingua";
import { Ricerca } from "./Ricerca";
import { AiutoBarra, AiutoStrato } from "./Aiuto";
import { FeedbackLayer } from "./FeedbackLayer";

const { Sider, Header, Content } = Layout;

/** Voce del menu attiva per il percorso: la più lunga che ne è prefisso. */
const VOCI = ["/", "/redflag", "/redflag/segnalazioni", "/redflag/azioni", "/redflag/report", "/claim", "/fornitori", "/proposte", "/admin/accessi", "/admin/liste", "/admin/registro", "/admin/sf"];
function voceAttiva(path: string) {
  if (path.startsWith("/redflag/nc/")) return "/redflag/segnalazioni";
  return VOCI.filter((v) => (v === "/" ? path === "/" : path === v || path.startsWith(`${v}/`))).sort((a, b) => b.length - a.length)[0] ?? "";
}

export function AppLayout() {
  const { me, logout } = useAuth();
  const { t } = useTranslation();
  const puo = usePuo();
  const nav = useNavigate();
  const loc = useLocation();
  const narrow = Grid.useBreakpoint().lg === false;
  const [drawer, setDrawer] = useState(false);
  useLinguaUtente(me?.id);
  // ambiente (QA_ENV del backend): si mostra accanto alla versione, mai in produzione
  const config = useQuery({ queryKey: ["auth-config"], queryFn: () => api.get<{ ambiente?: string }>("/auth/config"), staleTime: Infinity });
  const ambiente = config.data?.ambiente && config.data.ambiente !== "prod" ? config.data.ambiente : null;
  const version = useQuery({ queryKey: ["version"], queryFn: () => api.get<{ version: string; commit: string | null }>("/version"), staleTime: Infinity });
  const home = useQuery({ queryKey: ["home", linguaCorrente()], queryFn: () => api.get<HomeDto>("/portale/home", { lingua: linguaCorrente() }), enabled: !!me, staleTime: 30_000 });
  const daApprovare = useQuery({
    queryKey: ["proposte", "conteggio"],
    queryFn: () => api.get<PaginaDto<PropostaRigaDto>>("/proposte", { stato: "da_approvare", limit: 1 }),
    enabled: !!me && puo("proposta.decidi"),
    staleTime: 30_000,
  });
  // Feedback sul portale: l'utente è solo da mostrare nel riquadro «Contesto», l'identità vera la mette il backend.
  const utenteFeedback = useMemo(() => ({ name: me?.nome ?? "", email: me?.email ?? "" }), [me?.nome, me?.email]);
  if (!me) return null;

  const badge = (n: number | null | undefined) => (n ? <Badge count={n} size="small" color={palette.bad} style={{ boxShadow: "none" }} /> : null);
  const voce = (key: string, icon: React.ReactNode, label: string, n?: number | null) => ({
    key,
    icon,
    label: (
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span>{label}</span>
        {badge(n)}
      </span>
    ),
  });
  type Voce = NonNullable<MenuProps["items"]>[number];
  const gruppo = (key: string, label: string, children: (Voce | false)[]): Voce | null => {
    const c = children.filter(Boolean) as Voce[];
    return c.length ? { type: "group", key, label, children: c } : null;
  };
  const items = [
    voce("/", <HomeOutlined />, t("menu.home")),
    gruppo("g-rf", t("menu.gruppoRedflag"), [
      puo("rf.vedi") && voce("/redflag", <BarChartOutlined />, t("menu.cruscotto")),
      puo("rf.vedi") && voce("/redflag/segnalazioni", <FlagOutlined />, t("menu.segnalazioni"), home.data?.kpi.rfAperti),
      puo("rf.vedi") && voce("/redflag/azioni", <CarryOutOutlined />, t("menu.azioni")),
      puo("report.vedi") && voce("/redflag/report", <FileTextOutlined />, t("menu.report")),
    ]),
    gruppo("g-cl", t("menu.gruppoClaim"), [
      puo("claim.vedi") && voce("/claim", <EuroOutlined />, t("menu.claim")),
      puo("fornitore.vedi") && voce("/fornitori", <ShopOutlined />, t("menu.fornitori")),
    ]),
    gruppo("g-arrivo", t("menu.gruppoInArrivo"), [{ key: "audit", icon: <AuditOutlined />, label: t("menu.audit"), disabled: true }]),
    gruppo("g-prop", t("menu.gruppoProposte"), [
      puo("proposta.decidi")
        ? voce("/proposte", <CheckSquareOutlined />, t("menu.daApprovare"), daApprovare.data?.totale)
        : puo("proposta.crea") && voce("/proposte", <CheckSquareOutlined />, t("menu.mieProposte")),
    ]),
    gruppo("g-admin", t("menu.gruppoAdmin"), [
      puo("admin") && voce("/admin/accessi", <TeamOutlined />, t("menu.accessi")),
      puo("admin") && voce("/admin/liste", <UnorderedListOutlined />, t("menu.liste")),
      puo("admin") && voce("/admin/registro", <ProfileOutlined />, t("menu.registro")),
      puo("admin") && voce("/admin/sf", <CloudServerOutlined />, t("menu.sfCasi")),
    ]),
  ].filter(Boolean) as MenuProps["items"];

  const menu = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "18px 18px 12px", color: "#fff" }}>
        <div style={{ fontWeight: 800, letterSpacing: 0.4, fontSize: 15 }}>BRANDART</div>
        <div style={{ fontSize: 17, fontWeight: 600, marginTop: 2 }}>{t("portale.nome")}</div>
        <div style={{ color: palette.navFg, fontSize: 11.5, marginTop: 2 }}>{t("portale.dominio")}</div>
      </div>
      <div style={{ flex: 1, overflow: "auto" }}>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[voceAttiva(loc.pathname)]}
          items={items}
          onClick={(e) => {
            if (e.key.startsWith("/")) nav(e.key);
            setDrawer(false);
          }}
        />
      </div>
      <div style={{ padding: "10px 18px 12px", color: palette.navFg, fontSize: 11, opacity: 0.7 }} className="mono">
        {version.data ? [version.data.version, version.data.commit, ambiente].filter(Boolean).join(" · ") : ""}
      </div>
    </div>
  );

  return (
    <FeedbackLayer user={utenteFeedback} version={version.data?.version ?? "dev"} commit={version.data?.commit ?? null}>
    <AiutoStrato me={me} vista={vistaDi(loc.pathname)}>
      <Layout style={{ minHeight: "100vh" }}>
        {narrow ? (
          <Drawer open={drawer} onClose={() => setDrawer(false)} placement="left" width={248} styles={{ body: { padding: 0, background: palette.nav } }} closable={false}>
            {menu}
          </Drawer>
        ) : (
          <Sider width={224} style={{ position: "sticky", top: 0, height: "100vh" }}>
            {menu}
          </Sider>
        )}
        <Layout style={{ minWidth: 0 }}>
          <Header
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 16px", borderBottom: `1px solid ${palette.hair}`, height: 56, position: "sticky", top: 0, zIndex: 50 }}
          >
            <Space style={{ minWidth: 0, flex: 1 }} styles={{ item: { minWidth: 0, flex: "0 1 auto" } }}>
              {narrow && <Button type="text" icon={<MenuOutlined />} onClick={() => setDrawer(true)} aria-label={t("menu.apri")} />}
              <Ricerca />
            </Space>
            <Space size={10}>
              <AiutoBarra compatto={narrow} />
              <SelettoreLingua connesso />
              {!narrow && (
                <div style={{ textAlign: "right", lineHeight: 1.2 }}>
                  <div style={{ fontWeight: 600 }}>{me.nome}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }} className="mono" title={me.ruoli.join(", ")}>
                    {me.ruoli.length > 2 ? `${me.ruoli.slice(0, 2).join(" · ")} +${me.ruoli.length - 2}` : me.ruoli.join(" · ")}
                  </Typography.Text>
                </div>
              )}
              <Button
                icon={<LogoutOutlined />}
                aria-label={t("barra.esci")}
                onClick={async () => {
                  await logout();
                  window.location.assign("/login");
                }}
              >
                {narrow ? "" : t("barra.esci")}
              </Button>
            </Space>
          </Header>
          <Content id="main" tabIndex={-1} style={{ padding: narrow ? 12 : 20, maxWidth: 1440, width: "100%", margin: "0 auto" }}>
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </AiutoStrato>
    </FeedbackLayer>
  );
}
